import { useSelector } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { useDispatch } from "react-redux";
import {
  setIsSearchTermTabDataFetched,
  setSearchTermTabLoader,
} from "../../../../../features/searchTermTabSlice";
import { STOperationEndPointLogic } from "../../../../../logic/PaidTrafficManagement/SearchTermOperation/searchTermOperationEndPointLogic";
import { useSearchParams } from "react-router-dom";

const QUERY_IDS = {
  SEARCH_TERM_WITHOUT_CLASSIFICATION: "5763513033162752",
  EXCLUSIVE_MISSING_CROSS_NEGATIVES: "5639087159508992",
  CONTRADICTING_SEARCH_TERM_DEFINITIONS: "5716929146781696",
  IDENTICAL_TO_EXACT_KEYWORD: "5715241090416640",
  CAPTURED_BY_MULTIPLE_KEYWORDS: "5737612971081728",
};

export const useSearchTermsTab = () => {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const selectedSharedUid = searchParams.get("selectedSite") || "";
  const selectedAccountSharedUid = searchParams.get("account") || "";

  const fetchSearchTermsTabData = async () => {
    dispatch(setSearchTermTabLoader(true));
    dispatch(setIsSearchTermTabDataFetched(false));

    try {
      const [responseOfUnclasssifiedST] = await Promise.all([
        STOperationEndPointLogic.getSTPerformanceUnclassified(
          oauthTokenHeader,
          QUERY_IDS.SEARCH_TERM_WITHOUT_CLASSIFICATION,
          selectedSharedUid,
          selectedAccountSharedUid
        ),
        STOperationEndPointLogic.getESTMissingCrossNegatives(
          oauthTokenHeader,
          QUERY_IDS.EXCLUSIVE_MISSING_CROSS_NEGATIVES,
          selectedSharedUid,
          selectedAccountSharedUid
        ),
        STOperationEndPointLogic.getContradictingSTDefinitions(
          oauthTokenHeader,
          QUERY_IDS.CONTRADICTING_SEARCH_TERM_DEFINITIONS,
          selectedSharedUid,
          selectedAccountSharedUid
        ),
        STOperationEndPointLogic.getIdenticalToExactKeyword(
          oauthTokenHeader,
          QUERY_IDS.IDENTICAL_TO_EXACT_KEYWORD,
          selectedSharedUid,
          selectedAccountSharedUid
        ),

        STOperationEndPointLogic.getCapturedByMultipleKeywords(
          oauthTokenHeader,
          QUERY_IDS.CAPTURED_BY_MULTIPLE_KEYWORDS,
          selectedSharedUid,
          selectedAccountSharedUid
        ),
      ]);

      const allResponses = {
        responseOfUnclasssifiedST: responseOfUnclasssifiedST,
      };

      return allResponses;
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to fetch search terms data. Please try again.",
          type: "error",
        })
      );
    } finally {
      dispatch(setSearchTermTabLoader(false));
      dispatch(setIsSearchTermTabDataFetched(true));
    }
  };

  // Function to create positive search term links
  const createPositiveSearchTermLinks = async (
    positiveDataList: any[],
    addNegative: boolean,
    type: "Exclusive" | "Allowed"
  ) => {
    let successCount = 0;
    let failCount = 0;

    for (const item of positiveDataList) {
      try {
        const body = {
          search_term: item.search_term,
          search_term_match_type: item.search_term_match_type,
          keyword_id: item.keyword_id,
          scope_shared_uid: item.scope_shared_uid,
          link_type: type,
          add_negative_to_other_ad_groups: addNegative,
        };

        const result = await STOperationEndPointLogic.createPositiveLinks(
          oauthTokenHeader,
          body
        );

        if (result?.reason === "Success") {
          successCount++;
          const negatives = result.applied_scope_negatives || 0;
          const negativeInfo =
            negatives > 0 ? ` (${negatives} negatives applied)` : "";

          dispatch(
            setDisplayErrorSnackBar({
              message: `Successfully added "${item.search_term}" as ${type}${negativeInfo}`,
              type: "success",
            })
          );
        } else {
          failCount++;
          dispatch(
            setDisplayErrorSnackBar({
              message: `Failed to add "${item.search_term}" as ${type}`,
              type: "error",
            })
          );
        }
      } catch (error) {
        failCount++;
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        dispatch(
          setDisplayErrorSnackBar({
            message: `Error adding "${item.search_term}" as ${type}: ${errorMessage}`,
            type: "error",
          })
        );
      }
    }

    return { successCount, failCount };
  };

  // Function to create negative search term links
  const createNegativeSearchTermLinks = async (reqBody: any) => {
    let successCount = 0;
    let failCount = 0;
    let failedSearchTermUids: string[] = []; // Add this to track failed UIDs

    try {
      const result = await STOperationEndPointLogic.addNegativeTermsToAdgroup(
        oauthTokenHeader,
        reqBody
      );

      const summary = result?.data?.summary;
      const results = result?.data?.results || [];

      // Handle success
      if (summary?.successful > 0) {
        successCount = summary.successful;
        const successfulTerms = results
          .filter((r: any) => r.status !== "failed")
          .map((r: any) => r.term);

        const message =
          successfulTerms.length === 1
            ? `Successfully added "${successfulTerms[0]}" as Negative`
            : `Successfully added ${successfulTerms.length} negative search terms`;

        dispatch(
          setDisplayErrorSnackBar({
            message,
            type: "success",
          })
        );
      }

      // Handle failures
      if (summary?.failed > 0) {
        failCount = summary.failed;
        const failedTerms = results.filter((r: any) => r.status === "failed");

        // Extract search term UIDs of failed terms
        failedSearchTermUids = failedTerms
          .map((failedTerm: any) => {
            const originalTerm = reqBody.terms.find(
              (t: any) => t.term === failedTerm.term
            );
            return originalTerm?.additional_info?.search_term_shared_uid;
          })
          .filter(Boolean);

        // Only show failure message if there are failed terms
        if (failedTerms.length > 0) {
          const isDuplicate = failedTerms.some((r: any) =>
            r.error?.includes("Duplicate entry")
          );

          // Build message with proper term names
          let message = "";
          if (failedTerms.length === 1) {
            message = `Failed to add "${failedTerms[0].term}" as Negative${
              isDuplicate ? " (already exists)" : ""
            }`;
          } else {
            message = `Failed to add ${
              failedTerms.length
            } negative search terms${
              isDuplicate ? " (some already exist)" : ""
            }`;
          }

          dispatch(
            setDisplayErrorSnackBar({
              message,
              type: "error",
            })
          );
        }
      }
    } catch (error) {
      failCount = reqBody.terms.length;
      // Extract search term shared uids of failed terms
      failedSearchTermUids = reqBody.terms
        .map((t: any) => t.additional_info?.search_term_shared_uid)
        .filter(Boolean);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";

      dispatch(
        setDisplayErrorSnackBar({
          message: `Error adding negative search terms: ${errorMessage}`,
          type: "error",
        })
      );
    }

    return { successCount, failCount, failedSearchTermUids };
  };

  // get Unclassified Search Terms
  const getSTPerformanceUnclassified = async () => {
    try {
      await STOperationEndPointLogic.getSTPerformanceUnclassified(
        oauthTokenHeader,
        QUERY_IDS.SEARCH_TERM_WITHOUT_CLASSIFICATION,
        selectedSharedUid,
        selectedAccountSharedUid
      );
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "Failed to fetch unclassified search terms. Please try again.",
          type: "error",
        })
      );
    }
  };

  // get Exclusive Missing Negatives Search Terms
  const getExclusiveSTMissingNegatives = async () => {
    try {
      await STOperationEndPointLogic.getESTMissingCrossNegatives(
        oauthTokenHeader,
        QUERY_IDS.EXCLUSIVE_MISSING_CROSS_NEGATIVES,
        selectedSharedUid,
        selectedAccountSharedUid
      );
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "Failed to access missing cross negatives. Please try again.",
          type: "error",
        })
      );
    }
  };

  // get Contradicting Search Terms Definitions
  const getContradictingSTDefinitions = async () => {
    try {
      await STOperationEndPointLogic.getContradictingSTDefinitions(
        oauthTokenHeader,
        QUERY_IDS.CONTRADICTING_SEARCH_TERM_DEFINITIONS,
        selectedSharedUid,
        selectedAccountSharedUid
      );
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "Failed to access contradicting definitions. Please try again.",
          type: "error",
        })
      );
    }
  };

  // get Identical to Exact Keyword Search Terms
  const getIdenticalToExactKeywordData = async () => {
    try {
      await STOperationEndPointLogic.getIdenticalToExactKeyword(
        oauthTokenHeader,
        QUERY_IDS.IDENTICAL_TO_EXACT_KEYWORD,
        selectedSharedUid,
        selectedAccountSharedUid
      );
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to access exact keyword data. Please try again.",
          type: "error",
        })
      );
    }
  };

  // get Identical to Exact Keyword Search Terms
  const getCapturedByMultipleKeywords = async () => {
    try {
      await STOperationEndPointLogic.getCapturedByMultipleKeywords(
        oauthTokenHeader,
        QUERY_IDS.CAPTURED_BY_MULTIPLE_KEYWORDS,
        selectedSharedUid,
        selectedAccountSharedUid
      );
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            "Failed to access multiple search terms that are marked as Allowed. Please try again.",
          type: "error",
        })
      );
    }
  };

  const activateOrdeactivateExistingLinks = async (
    data: any[],
    targetIsActive: boolean
  ) => {
    let successCount = 0;
    let failCount = 0;
    let successfulUids: string[] = []; // Track successful UIDs

    for (const sharedUid of data) {
      try {
        const deactivateReqBody = {
          shared_uid: sharedUid,
          target_is_active: targetIsActive,
          add_negative_to_other_ad_groups: false,
        };

        const deactivateStatus =
          await STOperationEndPointLogic.activeOrInactivePositiveLinks(
            deactivateReqBody,
            oauthTokenHeader
          );

        if (deactivateStatus?.reason === "Success") {
          successCount++;
          successfulUids.push(sharedUid); // Add to successful UIDs
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }

    return { successCount, failCount, successfulUids };
  };

  return {
    fetchSearchTermsTabData,
    createPositiveSearchTermLinks,
    createNegativeSearchTermLinks,
    getSTPerformanceUnclassified,
    getContradictingSTDefinitions,
    getExclusiveSTMissingNegatives,
    getIdenticalToExactKeywordData,
    getCapturedByMultipleKeywords,
    activateOrdeactivateExistingLinks,
  };
};
